import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useWagmiAuth } from 'app/providers';
import BigNumber from 'bignumber.js';
import { COLOR_STROKE, cratChain, isZeroAddress, logger, toDecimals, useAppDispatch } from 'shared';
import { ZERO_ADDRESS } from 'shared/constants';
import { formatUnits } from 'shared/lib/token/units';
import { LinearProgressWithLabel, TokenFormat } from 'shared/ui';
import { AwaitableButton } from 'shared/ui/buttons/awaitable-button';
import { NetworkGuardedButton } from 'shared/ui/buttons/network-guard/network-guard-button';
import { useAccount } from 'wagmi';

import { useCitSaleData, useDebouncedEstimate } from '../../api/hooks';
import { citCrowdsaleThunks, receiveToken } from '../../model';
import { HARD_CAP, HOW_TO_BUY } from '../constants';
import { ProgressLabel } from '../styled';

import { validateForm } from './lib/resolver';
import { Balance } from './balance';
import { BonusTip } from './bonus-tip';
import { AmountInput } from './input';
import { Receive } from './receive';
import { Referral } from './referral';
import { SaleFormData, ValidationContext } from './types';

const defaultValues: SaleFormData = {
  amount: '',
  referrer: '',
};

export const SaleForm = () => {
  const { address } = useAccount();
  const { state: authState } = useWagmiAuth();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const citRef = searchParams.get('citRef') || '';

  const { minPaymentAmount, citPrice, paymentToken, referralInfo, totalSold, isPaused, loading } = useCitSaleData();

  const resolver = useCallback((data: SaleFormData, ctx: ValidationContext) => {
    return { values: data, errors: validateForm(data, ctx) };
  }, []);

  const methods = useForm<SaleFormData>({
    defaultValues,
    context: {
      minPaymentAmount,
      paymentToken,
      userAddress: address,
    },
    resolver,
    mode: 'onChange',
  });

  const {
    getValues,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const amount = watch('amount');

  const isFormValid = !errors.amount && amount && Number(amount) > 0;

  const {
    data,
    error: estimateError,
    isLoading: isEstimateLoading,
  } = useDebouncedEstimate({
    paymentAmount: amount,
    enabled: Boolean(isFormValid && minPaymentAmount),
  });

  const handleMax = useCallback(() => {
    if (paymentToken.balance) {
      setValue('amount', formatUnits(paymentToken.balance, paymentToken.decimals), { shouldValidate: true });
    }
  }, [paymentToken.balance, paymentToken.decimals, setValue]);

  const onSubmit = async ({ amount: paymentAmount, referrer }: SaleFormData) => {
    try {
      await dispatch(
        citCrowdsaleThunks.buy({
          paymentAmount: BigInt(toDecimals(paymentAmount, paymentToken.decimals)),
          referrer: referrer || ZERO_ADDRESS,
        }),
      ).unwrap();
    } catch (err) {
      logger('citCrowdsaleWidget/onSubmit', err);
    }
  };

  const soldProgress = useMemo(() => {
    return totalSold ? new BigNumber((totalSold ?? 0).toString()).dividedBy(HARD_CAP).multipliedBy(100).toNumber() : 0;
  }, [totalSold]);

  useEffect(() => {
    if (!referralInfo) return;

    if (referralInfo.purchaseMade) {
      setValue('referrer', isZeroAddress(referralInfo.referrer) ? '' : referralInfo.referrer, { shouldValidate: true });
      return;
    }

    if (citRef) {
      setValue('referrer', citRef, { shouldValidate: true });
    }
  }, [citRef, referralInfo, searchParams, setValue]);

  return (
    <FormProvider {...methods}>
      <Box component="form" width="100%" display="flex" flexDirection="column" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h5" textAlign="center" letterSpacing="0.5%" className="secondary regular" mb={1.5}>
          CIT Price:{' '}
          <TokenFormat
            amount={citPrice?.toString()}
            decimals={paymentToken.decimals}
            component="span"
            nospace
            symbol="$"
            variant="inherit"
            className="primary regular"
          />
        </Typography>
        <LinearProgressWithLabel
          value={soldProgress}
          label={<ProgressLabel variant="h5">{totalSold ? `${soldProgress.toFixed(2)}%` : '0%'}</ProgressLabel>}
          aria-label="CIT Crowdsale Progress"
          wrapperSx={{ mr: 0 }}
          sx={{
            height: 40,
            borderRadius: '30px',
            border: `6px solid ${COLOR_STROKE}`,
          }}
        />
        <Controller
          name="amount"
          control={control}
          render={({ field: { onChange, value } }) => (
            <AmountInput
              decimals={paymentToken.decimals}
              value={value}
              onChange={onChange}
              spellCheck={false}
              placeholder="0.00"
              fullWidth
              onMax={authState.isAuthorized ? handleMax : undefined}
              error={!!errors.amount || !!estimateError}
              helperText={errors.amount?.message || estimateError?.message}
              sx={{ mt: 1.5 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box component="img" width={40} height={32} src={paymentToken.image} />
                <Typography ml={1} display={{ xs: 'none', sm: 'block' }} variant="body1" className="primary" noWrap>
                  {paymentToken.name}
                </Typography>
              </Stack>
            </AmountInput>
          )}
        />

        <Stack
          mt={0.5}
          direction={{ xs: 'column-reverse', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <BonusTip currentBonus={data?.bonusPercentage?.toString()} loading={isEstimateLoading} />
          {authState.isAuthorized && (
            <Balance balance={paymentToken.balance} currency={paymentToken.name} decimals={paymentToken.decimals} />
          )}
        </Stack>

        <Receive
          finalAmount={data?.finalAmount}
          bonus={data?.bonus}
          decimals={receiveToken.decimals}
          loading={isEstimateLoading}
        />

        <NetworkGuardedButton
          targetChainId={cratChain.id}
          variant="contained"
          fullWidth
          sx={{ px: '16px', maxWidth: 190, margin: '0 auto' }}
        >
          <AwaitableButton
            action={handleSubmit(onSubmit)}
            isDisabled={!authState.isAuthorized || !methods.formState.isValid || isPaused || !!estimateError}
            isLoading={
              isEstimateLoading ||
              loading.isBalanceLoading ||
              loading.isPublicSaleInfoLoading ||
              loading.isReferralInfoLoading
            }
            fullWidth
          >
            Buy
          </AwaitableButton>
        </NetworkGuardedButton>

        <Typography
          component="a"
          href={HOW_TO_BUY}
          referrerPolicy="no-referrer"
          rel="noreferrer"
          target="_blank"
          className="secondary regular"
          sx={{
            textDecoration: 'underline',
            margin: '24px auto',
          }}
        >
          How to BUY $CIT?
        </Typography>

        {authState.isAuthorized && (
          <Referral
            earned={referralInfo?.earned}
            address={authState.address}
            purchased={!!referralInfo?.purchaseMade}
          />
        )}
      </Box>
    </FormProvider>
  );
};
